import { IceDevTools } from "@ice/react";
import { LicenseInfo } from "@mui/x-license";
import { QueryClientProvider } from "@tanstack/react-query";
import { VaporThemeProvider } from "@vapor/react-material";
import {
    AdapterDateFns,
    LocalizationProvider,
} from "@vapor/react-x-date-pickers";
import it from "date-fns/locale/it";
import dayjs from "dayjs";
import { BrowserRouter } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useGovernmentAgencyHubAppQueryClient } from "./core/api/utils/useGovernmentAgencyHubAppQueryClient";
import "./i18n/config";
import { Home } from "./ui/views/home/Home";
import { ViewWrapper } from "./ui/views/ViewWrapper";

LicenseInfo.setLicenseKey(import.meta.env.VITE_APP_CI_MUI_TOKEN as string);
dayjs.locale("it"); // TODO make dynamic based on user locale

const GovernmentAgenyHubAppStandaloneWrapper = () => {
    const queryClient = useGovernmentAgencyHubAppQueryClient();

    return (
        <QueryClientProvider client={queryClient}>
            <ViewWrapper>
                <Home />
            </ViewWrapper>
            {/* Sibling of ViewWrapper, never inside it. react-toastify queues
                toasts emitted with no container mounted and replays them once
                one subscribes, so the transient unmounts behind the loaders
                lose nothing — but on the terminal branches ServiceNotActive
                and DataNotAvailable the container would never mount at all,
                and the queue would never be drained. Those are exactly the
                branches a bootstrap error leads to. */}
            <ToastContainer />
        </QueryClientProvider>
    );
};

export const GovernmentAgenyHubAppStandalone = () => {
    return (
        <VaporThemeProvider>
            {/* Official ICE bootstrap for standalone: creates the SDK with
                the given environment and observability disabled (no OTLP
                traffic locally). In module mode it's the shell that
                initializes the SDK. Must be rendered BEFORE the rest of the
                tree: the ICE hooks require the SDK to already exist.

                `environment` is hardcoded and deliberately does NOT follow
                VITE_APP_ENV, because the two vocabularies do not line up.
                CreateSdkConfig["environment"] is
                `"dev" | "test" | "demo" | "prod"`, while VITE_APP_ENV reads
                "development" (.env.local, .env.development), "test"
                (.env.staging) and "prod" (.env.production): two of the three
                match, "development" does not, and "demo" has no env file at
                all. Wiring it up therefore needs a real lookup table with a
                fallback for an unrecognised value, not an expression.

                What the hardcoding costs: a `build:test` bundle served by
                `preview:test` bootstraps the SDK against the DEV platform,
                since the authorizer URL is derived from this value. Nearly moot
                in practice — a standalone bundle has no shell context and never
                leaves ViewWrapper's loader (see docs/runbook.md) — but it is
                the thing to fix first the day standalone gets a real session. */}
            <IceDevTools environment="dev" />
            <LocalizationProvider
                dateAdapter={AdapterDateFns}
                adapterLocale={it}
            >
                <BrowserRouter>
                    <GovernmentAgenyHubAppStandaloneWrapper />
                </BrowserRouter>
            </LocalizationProvider>
        </VaporThemeProvider>
    );
};
